import { FC, useMemo } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslate } from 'hooks/useTranslate';

import { State } from 'redux/initialState';
import { useGetActualSociety } from 'hooks/useGetActualSociety';

import menuTypes from 'constants/menuTypes';

import Page from 'modules/_shared/components/Pages/Page';
import Breadcrumb, {
  BreadcrumbItem,
} from 'modules/_shared/components/Breadcrumb';
import GoBackButton from 'modules/_shared/components/Buttons/GoBackButton';
import { useGetMenu } from 'modules/administratives/hooks/useGetMenu';

import '../../styles/Trading.scss';

const EquityMarketTerms: FC = () => {
  const { t } = useTranslate();
  const { societyId } = useParams<{ societyId: string }>();
  const location = useLocation();

  const actualSociety = useSelector(
    ({ society }: State) => society?.actualSociety
  );

  useGetActualSociety(societyId);
  useGetMenu(menuTypes.NULL);

  const pq = location.search || '';

  const breadcrumbItems: BreadcrumbItem[] = useMemo(
    () => [
      { label: t('Dashboard'), to: '/' },
      {
        label: actualSociety?.name || '',
        to: `/sociedad/${societyId}`,
      },
      {
        label: t('EquityMarket'),
        to: `/mercado-de-acciones/${societyId}${pq}`,
      },
      { label: t('OfferConditions'), isActive: true },
    ],
    [t, actualSociety?.name, societyId, pq]
  );

  return (
    <Page id="equity-market-terms-page">
      <div className="nk-block-head-content mb-3">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      <div className="nk-block doc-condiciones">
        <div className="nk-block-head nk-block-head-sm mb-4">
          <h4 className="nk-block-title page-title mt-2 mb-1">
            {t('OfferConditionsPageTitle')}
          </h4>
          <p className="text-soft">
            {t('OfferConditionsPageDescription')}{' '}
            {t('OfferConditionsLastUpdate')}
          </p>
        </div>

        <div className="nav-secciones mb-4">
          <p className="mb-1 fs-13px text-soft">
            <strong>{t('InThisPage')}</strong>
          </p>
          <ul className="list-unstyled mb-0 fs-13px">
            <li>
              <button
                type="button"
                className="btn btn-link p-0 text-left"
                onClick={() =>
                  document
                    .getElementById('oferta-venta')
                    ?.scrollIntoView({ behavior: 'smooth' })
                }
              >
                {t('SellOfferConditionsTitle')}
              </button>
            </li>
            <li>
              <button
                type="button"
                className="btn btn-link p-0 text-left"
                onClick={() =>
                  document
                    .getElementById('oferta-compra')
                    ?.scrollIntoView({ behavior: 'smooth' })
                }
              >
                {t('BuyOfferConditionsTitle')}
              </button>
            </li>
            <li>
              <button
                type="button"
                className="btn btn-link p-0 text-left"
                onClick={() =>
                  document
                    .getElementById('aceptacion')
                    ?.scrollIntoView({ behavior: 'smooth' })
                }
              >
                {t('AcceptanceConditionsTitle')}
              </button>
            </li>
          </ul>
        </div>

        <div className="card card-bordered">
          <div className="card-inner p-4 p-md-5">
            <section id="oferta-venta" className="anchor-block">
              <h5>{t('SellOfferConditionsTitle')}</h5>
              <p
                dangerouslySetInnerHTML={{
                  __html: t('SellOfferConditionsIntro'),
                }}
              />

              <h6>{t('OffererRequirements')}</h6>
              <ul className="list mb-4">
                <li>{t('SellOfferReq1')}</li>
                <li>{t('SellOfferReq2')}</li>
                <li>{t('SellOfferReq3')}</li>
              </ul>

              <h6>{t('PublicationEffects')}</h6>
              <ul className="list mb-4">
                <li>{t('SellOfferEffect1')}</li>
                <li>{t('SellOfferEffect2')}</li>
                <li>{t('SellOfferEffect3')}</li>
              </ul>

              <h6>{t('PriceAndPayment')}</h6>
              <p>{t('SellOfferPriceText')}</p>
            </section>

            <hr className="my-4" />

            <section id="oferta-compra" className="anchor-block">
              <h5>{t('BuyOfferConditionsTitle')}</h5>
              <p
                dangerouslySetInnerHTML={{
                  __html: t('BuyOfferConditionsIntro'),
                }}
              />

              <h6>{t('OffererRequirements')}</h6>
              <ul className="list mb-4">
                <li>{t('BuyOfferReq1')}</li>
                <li>{t('BuyOfferReq2')}</li>
                <li>{t('BuyOfferReq3')}</li>
              </ul>

              <h6>{t('PublicationEffects')}</h6>
              <ul className="list mb-4">
                <li>{t('BuyOfferEffect1')}</li>
                <li>{t('BuyOfferEffect2')}</li>
                <li>{t('BuyOfferEffect3')}</li>
              </ul>

              <h6>{t('OfferedPrice')}</h6>
              <p>{t('BuyOfferPriceText')}</p>
            </section>

            <hr className="my-4" />

            <section id="aceptacion" className="anchor-block">
              <h5>{t('AcceptanceConditionsTitle')}</h5>
              <p
                dangerouslySetInnerHTML={{
                  __html: t('AcceptanceConditionsIntro'),
                }}
              />

              <h6>{t('AcceptanceEffects')}</h6>
              <ul className="list mb-4">
                <li>{t('AcceptanceEffect1')}</li>
                <li>{t('AcceptanceEffect2')}</li>
                <li>{t('AcceptanceEffect3')}</li>
              </ul>

              <h6>{t('PartyObligations')}</h6>
              <ul className="list mb-4">
                <li>
                  <strong>{t('Seller')}:</strong> {t('SellerObligation')}
                </li>
                <li>
                  <strong>{t('Buyer')}:</strong> {t('BuyerObligation')}
                </li>
                <li>{t('TransferObligation')}</li>
              </ul>

              <h6>{t('TaxAndLegalAspects')}</h6>
              <p>{t('TaxAndLegalText')}</p>
            </section>

            <hr className="my-4" />

            <p className="text-soft mb-0">
              <em>{t('OfferConditionsDisclaimer')}</em>
            </p>
          </div>
        </div>

        <div className="mt-4">
          <GoBackButton icon="ni-arrow-left" />
        </div>
      </div>
    </Page>
  );
};

export default EquityMarketTerms;
